import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Divider, MenuItem, TextField, useTheme } from '@mui/material';

import {
  setListOpen,
  hasUnread,
  openThreads,
  useComments,
} from '@/designs-dev/comments/store';
import { Pill, Segment } from '@/designs-dev/inspect/Pill';
import {
  useInspect,
  type InspectMode,
} from '@/designs-dev/inspect/InspectContext';
import {
  viewportToParam,
  viewportWidthOptions,
  type Viewport,
} from '@/designs-dev/device/viewport';

// Figma-Dev-Mode-style (we call it drill mode) segmented toggle that sits under
// the screen: preview vs inspect (the design-system audit). Stages can pass
// `viewport`/`onViewportChange` to get a viewport-size selector — the device
// frame, a full-bleed fill, or a fixed breakpoint width.
//
// How you are looking at the design, and nothing about what it is showing: the
// prototype's state is StateSelector, its own pill beside this one.
const MODES: { mode: InspectMode; icon: string; label: string }[] = [
  { mode: 'view', icon: 'eye', label: 'preview' },
  { mode: 'inspect', icon: 'code', label: 'inspect' },
  { mode: 'comment', icon: 'message', label: 'comment (C)' },
];

const DrillModeToggle = ({
  viewport,
  onViewportChange,
}: {
  viewport?: Viewport;
  onViewportChange?: (v: Viewport) => void;
}) => {
  const { mode, setMode, setCurrent, setHovered } = useInspect();
  const comments = useComments();
  const { user } = useKindeAuth();
  const { listOpen } = comments;
  const theme = useTheme();

  return (
    <Pill>
      {MODES.map((m) => (
        <Segment
          key={m.mode}
          icon={m.icon}
          label={m.label}
          selected={mode === m.mode}
          count={m.mode === 'comment' ? openThreads(comments).length : 0}
          alert={m.mode === 'comment' && hasUnread(comments, user?.id)}
          onClick={() => {
            setMode(m.mode);
            if (m.mode !== 'inspect') {
              setCurrent(null);
              setHovered(null);
            }
          }}
        />
      ))}
      {/* In comment mode, the list of every thread, which stays shut until
          asked for so commenting doesn't take room from the design. */}
      {mode === 'comment' && (
        <>
          <Divider orientation='vertical' flexItem />
          <Segment
            icon='list'
            label='All comments'
            selected={listOpen}
            onClick={() => setListOpen(!listOpen)}
          />
        </>
      )}
      {viewport !== undefined && onViewportChange && (
        <>
          <Divider orientation='vertical' flexItem />
          <TextField
            slotProps={{ input: { disableUnderline: true } }}
            select
            size='small'
            variant='standard'
            value={viewportToParam(viewport)}
            onChange={(e) => {
              const v = e.target.value;
              onViewportChange(v === 'frame' || v === 'fill' ? v : Number(v));
            }}
            aria-label='Viewport size'
            sx={{ minWidth: 120, px: 1 }}
          >
            <MenuItem value='frame'>Frame</MenuItem>
            <MenuItem value='fill'>Fill</MenuItem>
            {viewportWidthOptions(theme).map((o) => (
              <MenuItem key={o.width} value={String(o.width)}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </>
      )}
    </Pill>
  );
};

export default DrillModeToggle;
